<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >

    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <Button
            v-if="getUserPermission('CREATE_ACCOUNT_CATEGORY')"
            :label="$t('Add')"
            size="md"
            @click="showModal('modal-lg')"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
          sm="12"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />

            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      ref="accountCategoryRef"
      :items="accountCategoryList"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      empty-text="No records available"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(dateCreated)="data">
        {{ toDateWithYear(data.value) }}
      </template>

      <template #cell(status)="data">
        <b-badge
          pill
          :variant="variantColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">
        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >

            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="getUserPermission('MODIFY_ACCOUNT_CATEGORY')"
              @click="onSelectAccountCategoryItem(data.item, $event.target, 'edit')"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">{{ $t('Modify') }}</span>
            </b-dropdown-item>

            <b-dropdown-item @click="onSelectAccountCategoryItem(data.item, $event.target, 'view')">
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50">{{ $t('View') }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-lg"
      ref="my-modal"
      :ok-title="buttonLabel"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      :ok-disabled="isLoading"
      centered
      size="lg"
      no-close-on-backdrop
      :title="modalTitle"
      @ok="handleSubmit"
      @hidden="resetModal"
    >
      <validation-observer ref="simpleRules">
        <b-form>
          <b-row class="match-height">
            <!-- Category Name -->
            <b-col md="6">
              <InputField
                id="category-name"
                v-model="categoryFormData.name"
                :label="$t('Category Name')"
                :placeholder="$t('Enter category name')"
                type="text"
                :error-name="$t('Category Name')"
                rules="required"
                for="category-name"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Category Name -->

            <!-- Level 1 -->
            <b-col md="6">
              <SelectField
                id="level-1"
                v-model="categoryFormData.parentCategory"
                :label="$t('Level 1')"
                :options="parentCategoryOptions"
                :error-name="$t('Level 1')"
                rules="required"
                :reduce="option => option.value"
                item="label"
                :placeholder="$t('Select')"
                :readonly="isEditable"
              />
            </b-col>
            <!-- Level 1-->

          </b-row>

          <b-row class="match-height">
            <!-- Select Level -->
            <b-col md="6">
              <SelectField
                id="select-level"
                v-model="categoryFormData.level"
                :label="$t('Select Level')"
                :options="specifylevelOptions"
                :error-name="$t('Select Level')"
                rules="required"
                item="label"
                :reduce="option => option.value"
                :placeholder="$t('Select')"
                :set-selected="getLevelThreeOptions"
                :readonly="isEditable"
              />
            </b-col>
            <!-- Select Level -->

            <!-- Parent Category -->
            <b-col
              v-if="isSelectLevelThree && isEditable"
              md="6"
            >
              <InputField
                id="parent-category"
                v-model="categoryFormData.level2Category"
                :label="$t('Parent Category')"
                type="text"
                for="parent-category"
                :readonly="isEditable"
              />
            </b-col>

            <b-col
              v-if="isSelectLevelThree && !isEditable"
              md="6"
            >
              <SelectField
                id="level2-category"
                v-model="categoryFormData.parentCode"
                :label="$t('Parent Category')"
                :options="levelTwoOptions"
                :error-name="$t('Level 2')"
                rules="required"
                :reduce="option => option.value"
                item="label"
                :placeholder="$t('Select')"
                :readonly="isEditable"
              />
            </b-col>
            <!-- Parent Category-->

            <!-- Description-->
            <b-col md="6">
              <InputField
                id="description"
                v-model="categoryFormData.description"
                :label="$t('Description')"
                :placeholder="$t('Enter description')"
                type="text"
                :error-name="$t('Description')"
                rules="required"
                for="description"
                :readonly="isReadonly"
              />
            </b-col>
            <!--Description -->
          </b-row>

        </b-form>
      </validation-observer>
      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      no-close-on-backdrop
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchAccountCategory"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!-- Name  -->
        <b-col md="6">
          <InputField
            id="name"
            v-model="filter.name"
            :label="$t('Name')"
            :placeholder="$t('Enter name')"
            type="text"
            onpaste="return false;"
            :is-allow-special-characters="false"
          />
        </b-col>
        <!-- Name -->

        <!-- Code -->
        <b-col md="6">
          <InputField
            id="code"
            v-model="filter.code"
            :label="$t('Code')"
            :placeholder="$t('Enter code')"
            type="text"
          />
        </b-col>
        <!-- Code -->

        <!-- Level -->
        <b-col md="6">
          <SelectField
            id="level"
            v-model="filter.level"
            :label="$t('Level')"
            :placeholder="$t('Select level')"
            :reduce="option => option.value"
            :options="levelOptions"
            item="label"
          />
        </b-col>
        <!-- Level -->

        <!--  Parent Category code -->
        <b-col md="6">
          <SelectField
            id="parent-category"
            v-model="filter.parentCategory"
            :label="$t('Level 1')"
            :placeholder="$t('Select parent category')"
            :options="parentCategoryOptions"
            :reduce="option => option.value"
            item="label"
          />
        </b-col>
        <!--  Parent Category code -->
      </b-row>
    </b-modal>
    <!-- Filter modal -->
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BBadge,
  BDropdown, BModal, BForm, BSpinner, BDropdownItem,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapActions } from 'vuex'
import { permissionMixin } from '@core/mixins/permission'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import InputField from '../../forms/InputField.vue'
import SelectField from '../../forms/SelectField.vue'
import { toDateWithYear } from '../../../helpers/formatDate'
import {
  parentCategory, specifyLevel, parentCodeList, accessLevel,
} from '../../../helpers/data'
import Button from '../../components/button/Button.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BTable,
    BDropdown,
    BSpinner,
    BDropdownItem,
    BModal,
    BBadge,
    BForm,
    InputField,
    SelectField,
    ValidationObserver,
    TablePagination,
    Button,
    TableEntries,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      toDateWithYear,
      sortBy: 'code',
      isReadonly: false,
      buttonLabel: this.$t('Save'),
      modalTitle: this.$t('Create Account Category'),
      isSelectLevelThree: false,
      isBusy: false,
      required,
      searchURL: '',
      sortDesc: false,
      isEditable: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'name', sortable: true, label: 'Name' },
        { key: 'description', sortable: true, label: 'Description' },
        { key: 'level', sortable: true, label: 'Level' },
        { key: 'code', sortable: true, label: 'Code' },
        { key: 'parentName', sortable: true, label: 'Parent Category' },
        { key: 'dateCreated', sortable: true, label: 'Date Created' },
        { key: 'actions', label: 'Actions' },
      ],
      levelTwoOptions: [],
      categoryFormData: {
        name: '',
        parentCategory: '',
        description: '',
        level: '',
        parentCode: '',
        id: '',
      },
      filter: {
        name: '',
        code: '',
        level: '',
        parentCategory: '',
      },
      allLevelTwoOptions: [],
      parentCodeList,
      accountCategoryList: [],
    }
  },
  computed: {
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    parentCategoryOptions() {
      return parentCategory.map(item => ({ label: this.$t(item.label), value: item.value }))
    },
    specifylevelOptions() {
      return specifyLevel.map(item => ({ label: this.$t(item.label), value: item.value }))
    },
    levelOptions() {
      return accessLevel.map(item => ({ label: this.$t(item.label), value: item.value })).slice(0, 3)
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchAccountCategory()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchAccountCategory()
    },
  },
  mounted() {
    this.fetchAccountCategory()
  },
  methods: {
    ...mapActions({
      getAccountCategory: 'account/getAccountCategoryList',
      updateAccountCategory: 'account/updateAccountCategoryAction',
      createAccountCategory: 'account/createAccountCategoryAction',
      getLevelThree: 'account/getLevelThreeList',
    }),
    async getLevelThreeOptions(selectedValue) {
      try {
        const level1 = this.categoryFormData.parentCategory
        if (selectedValue === 3) {
          this.isSelectLevelThree = true
          const response = await this.getLevelThree({ level1, accountCategoryLevel: selectedValue })
          const { data, status } = response
          if (status === 200 && data.data) {
            this.levelTwoOptions = data.data.map(item => ({ label: item.description, value: item.code }))
            this.allLevelTwoOptions = data.data
          }
        } else {
          this.isSelectLevelThree = false
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    getParentCode(value) {
      const parentCode = this.parentCodeList.find(item => item.label === value).code
      return parentCode
    },
    async accountCategoryAction() {
      try {
        this.isLoading = true
        if (this.categoryFormData.parentCode) {
          this.categoryFormData.parentCode = this.categoryFormData.level === 2
            ? this.getParentCode(this.categoryFormData.parentCategory)
            : this.categoryFormData.parentCode.toString()
        }

        const response = this.buttonLabel === this.$t('Save')
          ? await this.createAccountCategory(this.categoryFormData)
          : await this.updateAccountCategory({
            name: this.categoryFormData.name,
            description: this.categoryFormData.description,
            id: this.categoryFormData.id,
          })

        const { status, data } = response
        if (status === 200) {
          this.fetchAccountCategory()
          this.hideModal('modal-lg')
          const successMessage = this.buttonLabel === this.$t('Save')
            ? this.$t('Your request to Save Account Category was successful')
            : this.$t('Your request to Update Account Category was successful')
          this.alertSuccess({ text: successMessage })
        } else {
          this.alertError({
            text: this.buttonLabel === this.$t('Save')
              ? this.$t(`Your request to Save Account Category failed. Reason: ${data.message}`)
              : this.$t(`Your request to Update Account Category failed. Reason: ${data.message}`),
          })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isLoading = false
      }
    },
    handleSubmit(bvModalEvt) {
      try {
        bvModalEvt.preventDefault()
        this.$refs.simpleRules.validate().then(async success => {
          if (success) {
            const actionText = this.buttonLabel === this.$t('Save')
              ? this.$t('Are you sure you want to Save this Account Category?')
              : this.$t('Are you sure you want to Update this Account Category?')
            this.confirm(this.accountCategoryAction, { text: actionText })
          }
        })
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isLoading = false
        this.hideModal('modal-lg')
      }
    },

    async onSelectAccountCategoryItem(items, activity, val) {
      try {
        const level = this.specifylevelOptions.find(selectedLevel => selectedLevel.value === items.level)
        this.categoryFormData = { ...items, level }
        this.isSelectLevel = items.level === 3 ? this.isSelectLevelThree = true : this.isSelectLevelThree = false
        if (items.level === 3) {
          this.categoryFormData.level2Category = items.parentName
          this.isSelectLevelThree = true
        } else {
          this.isSelectLevelThree = false
        }

        this.isReadonly = val !== 'edit'
        this.isEditable = true
        this.modalTitle = val === 'edit' ? this.$t('Edit Account Category') : this.$t('View Account Category')
        this.buttonLabel = this.$t('Update')
        this.$root.$emit('bv::show::modal', 'modal-lg')
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async fetchAccountCategory() {
      this.isBusy = true
      this.hideModal('modal-filter')
      try {
        const response = await this.getAccountCategory({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.accountCategoryList = data.data
          this.totalCount = data.recordCount
        } else {
          this.accountCategoryList = []
          this.totalCount = 0
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchAccountCategory()
    },
    resetModal() {
      this.categoryFormData = { ...null }
      this.isSelectLevelThree = false
      this.isReadonly = false
      this.isEditable = false
      this.isLoading = false
      this.modalTitle = this.$t('Create Account Category')
      this.buttonLabel = this.$t('Save')
    },
  },
}
</script>

<style scoped>
.successColor {
  color: #0DD32E;
  font-weight: 600;
}
.successApprovalColor {
  color: #FF7D33;
  font-weight: 600;
}
.failedColor {
  color: #E5471D;
  font-weight: 600;
}
</style>
